// src/components/welcome/WelcomePage.jsx
// 공개 웰컴 페이지 — 취향 선택 + 마케팅 동의

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fsGetWelcomeContext, fsSubmitWelcome } from '../../services/firebase';

const INTERESTS = [
  { id: 'bbq',    label: '바비큐',     emoji: '🔥' },
  { id: 'pool',   label: '수영장',     emoji: '🌊' },
  { id: 'quiet',  label: '조용한 객실', emoji: '🛏️' },
  { id: 'family', label: '가족 여행',  emoji: '👨‍👩‍👧' },
  { id: 'group',  label: '단체 모임',  emoji: '🎉' },
  { id: 'etc',    label: '기타',       emoji: '✨' },
];

export default function WelcomePage() {
  const { code } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('form'); // 'form'|'done'|'error'|'used'
  const [selected, setSelected] = useState([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fsGetWelcomeContext(code)
      .then(data => {
        if (!data) setStep('error');
        else if (data.used) setStep('used');
        else setCtx(data);
      })
      .catch(() => setStep('error'))
      .finally(() => setLoading(false));
  }, [code]);

  function toggle(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await fsSubmitWelcome(code, { interests: selected, marketingConsent: consent });
      setStep('done');
    } catch {
      alert('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  }

  const container = {
    maxWidth: 480, margin: '0 auto', minHeight: '100dvh',
    background: '#fafafa', display: 'flex', flexDirection: 'column',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  if (loading) return (
    <div style={{ ...container, alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <div style={{ fontSize: 36 }}>🏡</div>
      <div style={{ fontSize: 15, color: '#6b7280' }}>불러오는 중...</div>
    </div>
  );

  if (step === 'error') return (
    <div style={{ ...container, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
      <div style={{ fontSize: 48 }}>😥</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>유효하지 않은 링크입니다</div>
      <div style={{ fontSize: 14, color: '#6b7280', textAlign: 'center' }}>링크가 만료되었거나 올바르지 않습니다.</div>
    </div>
  );

  if (step === 'used') return (
    <div style={{ ...container, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}>
      <div style={{ fontSize: 48 }}>✅</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>이미 선택하셨습니다</div>
      <div style={{ fontSize: 14, color: '#6b7280', textAlign: 'center' }}>소중한 관심 감사합니다 🙏</div>
    </div>
  );

  if (step === 'done') return (
    <div style={{ ...container, alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
      <div style={{ fontSize: 64 }}>🎉</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>감사합니다!</div>
      <div style={{ fontSize: 15, color: '#6b7280', textAlign: 'center', lineHeight: 1.7 }}>
        {ctx?.customerName ? `${ctx.customerName}님의 ` : ''}관심 사항이 전달되었습니다.<br />
        다음 방문 때 맞춤 혜택을 준비할게요 🙏
      </div>
      {consent && (
        <div style={{
          marginTop: 8, padding: '12px 20px', borderRadius: 12,
          background: '#f0fdf4', border: '1px solid #bbf7d0',
          fontSize: 13, color: '#15803d', fontWeight: 600, textAlign: 'center',
        }}>
          ✓ 할인·이벤트 안내 수신에 동의해주셨습니다
        </div>
      )}
    </div>
  );

  return (
    <div style={container}>
      {/* 헤더 */}
      <div style={{
        padding: '20px 20px 16px',
        background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
        color: 'white',
      }}>
        <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>맞춤 혜택 안내</div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{ctx?.pensionName || '숙소'}</div>
        {ctx?.customerName && (
          <div style={{ fontSize: 14, opacity: 0.9, marginTop: 4 }}>
            {ctx.customerName}님, 다시 만나 반가워요 🙏
          </div>
        )}
      </div>

      {/* 본문 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 100px' }}>

        {/* 관심사 선택 */}
        <div style={{
          background: 'white', borderRadius: 16, padding: '20px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 14,
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: '#111827' }}>
            어떤 활동에 관심 있으신가요?
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>복수 선택 가능합니다</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
            {INTERESTS.map(({ id, label, emoji }) => {
              const on = selected.includes(id);
              return (
                <button
                  key={id}
                  onClick={() => toggle(id)}
                  style={{
                    padding: '14px 8px', borderRadius: 12,
                    border: `2px solid ${on ? '#16a34a' : '#e5e7eb'}`,
                    background: on ? '#f0fdf4' : 'white',
                    cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: 28 }}>{emoji}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: on ? '#15803d' : '#374151' }}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 마케팅 동의 */}
        <div style={{
          background: 'white', borderRadius: 16, padding: '18px 20px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.07)', marginBottom: 14,
        }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
            <div
              onClick={() => setConsent(v => !v)}
              style={{
                width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
                border: `2px solid ${consent ? '#16a34a' : '#d1d5db'}`,
                background: consent ? '#16a34a' : 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s', cursor: 'pointer',
              }}
            >
              {consent && <span style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>✓</span>}
            </div>
            <div onClick={() => setConsent(v => !v)}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 3 }}>
                할인·이벤트 안내를 받으시겠습니까?
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>
                동의 시 {ctx?.pensionName || '숙소'}에서 특별 할인 및 이벤트 소식을<br />
                문자로 받아보실 수 있습니다 (언제든 거부 가능)
              </div>
            </div>
          </label>
        </div>

      </div>

      {/* 제출 버튼 */}
      <div style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480,
        padding: '12px 20px 28px', background: 'white',
        borderTop: '1px solid #f3f4f6',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
      }}>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%', padding: '15px', borderRadius: 14, border: 'none',
            fontSize: 16, fontWeight: 800, cursor: submitting ? 'not-allowed' : 'pointer',
            background: !submitting ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' : '#e5e7eb',
            color: !submitting ? 'white' : '#9ca3af',
            boxShadow: !submitting ? '0 4px 16px rgba(34,197,94,0.35)' : 'none',
            transition: 'all 0.15s',
          }}
        >
          {submitting ? '제출 중...' : '🎯 관심사 제출하기'}
        </button>
      </div>
    </div>
  );
}
